import { useState, useEffect } from 'react';
import { apiFetch } from '../lib/api';
import { useParams, useNavigate } from 'react-router-dom';

interface User {
  id: number;
  name: string;
  relationship: string;
}

interface Diary {
  id: number;
  user_id: number | null;
  diary_type: string;
  diary_date: string;
  layer_1: string;
  layer_2: string;
  layer_3: string;
  conversation_count: number;
  user_name?: string;
}

// 生成日曆數據
function generateCalendarDays(year: number, month: number, diaryDates: Set<string>) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startPadding = firstDay.getDay();
  const totalDays = lastDay.getDate();

  const days: { date: number | null; hasDiary: boolean; dateStr: string }[] = [];

  // 前面的空白
  for (let i = 0; i < startPadding; i++) {
    days.push({ date: null, hasDiary: false, dateStr: '' });
  }

  // 日期
  for (let d = 1; d <= totalDays; d++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    days.push({
      date: d,
      hasDiary: diaryDates.has(dateStr),
      dateStr
    });
  }

  return days;
}

export default function Diaries() {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<number | 'general'>(
    userId ? parseInt(userId) : 'general'
  );
  const [diaries, setDiaries] = useState<Diary[]>([]);
  const [selectedDiary, setSelectedDiary] = useState<Diary | null>(null);
  const [loading, setLoading] = useState(true);

  // 日曆狀態
  const [calendarDate, setCalendarDate] = useState(new Date());

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchDiaries();
  }, [selectedUser]);

  const fetchUsers = async () => {
    try {
      const res = await apiFetch('/api/users');
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchDiaries = async () => {
    setLoading(true);
    try {
      const url = selectedUser === 'general'
        ? '/api/diaries/general'
        : `/api/diaries/personal/${selectedUser}`;

      const res = await apiFetch(url);
      if (res.ok) {
        const data = await res.json();
        setDiaries(data);
        if (data.length > 0) {
          setSelectedDiary(data[0]);
        } else {
          setSelectedDiary(null);
        }
      }
    } catch (error) {
      console.error('Failed to fetch diaries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUserChange = (userId: number | 'general') => {
    setSelectedUser(userId);
    if (userId === 'general') {
      navigate('/diaries');
    } else {
      navigate(`/diaries/${userId}`);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleDateClick = (dateStr: string) => {
    const diary = diaries.find(d => d.diary_date === dateStr);
    if (diary) {
      setSelectedDiary(diary);
    }
  };

  // 日曆數據
  const diaryDates = new Set(diaries.map(d => d.diary_date.split('T')[0]));
  const calendarDays = generateCalendarDays(
    calendarDate.getFullYear(),
    calendarDate.getMonth(),
    diaryDates
  );

  const weekdays = ['日', '一', '二', '三', '四', '五', '六'];

  return (
    <div className="space-y-6 animate-fadeUp">
      {/* 人物選擇標籤 */}
      <div
        className="flex flex-wrap gap-2 p-4 rounded-2xl"
        style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
      >
        <button
          onClick={() => handleUserChange('general')}
          className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
          style={{
            background: selectedUser === 'general' ? 'rgba(233, 30, 140, 0.15)' : 'var(--bg-surface-2)',
            color: selectedUser === 'general' ? '#e91e8c' : 'var(--text-secondary)',
            border: `1px solid ${selectedUser === 'general' ? 'rgba(233, 30, 140, 0.3)' : 'var(--border)'}`,
          }}
        >
          📔 總日記
        </button>
        {users.map(user => (
          <button
            key={user.id}
            onClick={() => handleUserChange(user.id)}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
            style={{
              background: selectedUser === user.id ? 'rgba(233, 30, 140, 0.15)' : 'var(--bg-surface-2)',
              color: selectedUser === user.id ? '#e91e8c' : 'var(--text-secondary)',
              border: `1px solid ${selectedUser === user.id ? 'rgba(233, 30, 140, 0.3)' : 'var(--border)'}`,
            }}
          >
            {user.name}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 左側：日曆 */}
        <div
          className="rounded-2xl p-5"
          style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
        >
          {/* 月份導航 */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1))}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-all hover:bg-bg-surface-2"
              style={{ color: 'var(--text-secondary)' }}
            >
              ←
            </button>
            <span className="text-base font-medium text-text-primary">
              {calendarDate.getFullYear()} 年 {calendarDate.getMonth() + 1} 月
            </span>
            <button
              onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1))}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-all hover:bg-bg-surface-2"
              style={{ color: 'var(--text-secondary)' }}
            >
              →
            </button>
          </div>

          {/* 星期標題 */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekdays.map(day => (
              <div key={day} className="text-center text-xs text-text-muted py-2">
                {day}
              </div>
            ))}
          </div>

          {/* 日期網格 */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((day, idx) => (
              <button
                key={idx}
                onClick={() => day.date && day.hasDiary && handleDateClick(day.dateStr)}
                disabled={!day.date || !day.hasDiary}
                className="aspect-square rounded-lg flex items-center justify-center text-sm relative transition-all"
                style={{
                  background: selectedDiary?.diary_date.startsWith(day.dateStr) ? 'rgba(233, 30, 140, 0.2)' : 'transparent',
                  color: day.date ? (day.hasDiary ? '#e8e8f0' : '#555570') : 'transparent',
                  cursor: day.date && day.hasDiary ? 'pointer' : 'default',
                }}
              >
                {day.date}
                {day.hasDiary && (
                  <div
                    className="absolute bottom-1 w-1.5 h-1.5 rounded-full"
                    style={{ background: '#e91e8c' }}
                  />
                )}
              </button>
            ))}
          </div>

          {/* 日記列表 */}
          <div className="mt-6 pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
            <h3 className="text-sm text-text-secondary mb-3">最近日記</h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {loading ? (
                <div className="text-center py-4">
                  <div className="loading-spinner mx-auto"></div>
                </div>
              ) : diaries.length > 0 ? (
                diaries.slice(0, 5).map(diary => (
                  <button
                    key={diary.id}
                    onClick={() => setSelectedDiary(diary)}
                    className="w-full text-left p-3 rounded-xl transition-all"
                    style={{
                      background: selectedDiary?.id === diary.id ? 'rgba(233, 30, 140, 0.1)' : 'var(--bg-surface-2)',
                      border: `1px solid ${selectedDiary?.id === diary.id ? 'rgba(233, 30, 140, 0.2)' : 'transparent'}`,
                    }}
                  >
                    <div className="text-sm text-text-primary">
                      {formatDate(diary.diary_date)}
                    </div>
                    <div className="text-xs text-text-muted mt-1">
                      💬 {diary.conversation_count} 次對話
                    </div>
                  </button>
                ))
              ) : (
                <div className="text-center py-4 text-text-muted text-sm">
                  還沒有日記
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 右側：日記內容 */}
        <div className="lg:col-span-2 space-y-4">
          {selectedDiary ? (
            <>
              {/* 日期標題 */}
              <div
                className="rounded-2xl p-5"
                style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-serif text-text-primary">
                      {formatDate(selectedDiary.diary_date)}
                    </h2>
                    <p className="text-sm text-text-muted mt-1">
                      {selectedUser === 'general'
                        ? '包小容的總日記'
                        : `給 ${users.find(u => u.id === selectedUser)?.name} 的日記`
                      }
                    </p>
                  </div>
                  <div className="text-sm text-text-muted">
                    💬 {selectedDiary.conversation_count} 次對話
                  </div>
                </div>
              </div>

              {/* Layer 1 - 今日流水帳 */}
              <div
                className="rounded-2xl p-5"
                style={{
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border)',
                  borderLeft: '4px solid #00d4ff',
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="layer-tag layer1">
                    <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                    Layer 1
                  </span>
                  <span className="text-sm text-text-muted">今日流水帳</span>
                </div>
                <p className="text-text-primary whitespace-pre-wrap leading-relaxed">
                  {selectedDiary.layer_1 || '（今天沒有特別記錄）'}
                </p>
              </div>

              {/* Layer 2 - 值得記住的 */}
              <div
                className="rounded-2xl p-5"
                style={{
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border)',
                  borderLeft: '4px solid #a855f7',
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="layer-tag layer2">
                    <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                    Layer 2
                  </span>
                  <span className="text-sm text-text-muted">值得記住的</span>
                </div>
                <p className="text-text-primary whitespace-pre-wrap leading-relaxed">
                  {selectedDiary.layer_2 || '（今天沒有特別值得記住的事）'}
                </p>
              </div>

              {/* Layer 3 - 明天的態度 */}
              <div
                className="rounded-2xl p-5"
                style={{
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border)',
                  borderLeft: '4px solid #e91e8c',
                  boxShadow: '0 0 30px rgba(233, 30, 140, 0.08)',
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="layer-tag layer3">
                    <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                    Layer 3
                  </span>
                  <span className="text-sm text-text-muted">明天的態度</span>
                  <span
                    className="ml-auto text-xs px-2 py-1 rounded-full flex items-center gap-1"
                    style={{ background: 'rgba(0, 230, 118, 0.1)', color: '#00e676' }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                    已注入 Prompt
                  </span>
                </div>
                <p
                  className="whitespace-pre-wrap leading-relaxed italic"
                  style={{ color: '#f472b6' }}
                >
                  {selectedDiary.layer_3 || '（明天繼續做自己就好）'}
                </p>
              </div>
            </>
          ) : (
            <div
              className="rounded-2xl p-12 text-center"
              style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
            >
              <div className="relative w-20 h-20 mx-auto mb-6">
                <div
                  className="absolute inset-0 rounded-full animate-breathe"
                  style={{ background: 'radial-gradient(circle, rgba(233, 30, 140, 0.2) 0%, transparent 70%)' }}
                />
                <div
                  className="absolute inset-4 rounded-full animate-breathe flex items-center justify-center"
                  style={{
                    background: 'radial-gradient(circle at 30% 30%, #ff6bb3, #e91e8c)',
                    boxShadow: '0 0 30px rgba(233, 30, 140, 0.4)',
                    animationDelay: '0.2s',
                  }}
                >
                  <span className="text-white text-xl">📔</span>
                </div>
              </div>
              <p className="text-text-secondary">
                {loading ? '載入中...' : '選擇一篇日記來閱讀'}
              </p>
              <p className="text-text-muted text-sm mt-2">
                或者等我晚上想想再寫給你看
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
